import classNames from 'classnames';
import React, { FC, ReactNode } from 'react';

type ContentBlockProps = {
  wrapperID?: string;
  wrapperClass?: string;
  bgColor?: string;
  bgHeight?: string;
  containerClass?: string;
  partialBg?: boolean;
  hideoverflow?: boolean;
  padtop?: boolean;
  padbottom?: boolean;
  padright?: boolean;
  padleft?: boolean;
  bleedleft?: boolean;
  bleedright?: boolean;
  bleedrightdesktop?: boolean;
  bleedleftdesktop?: boolean;
  hasDots?: boolean;
  hasDotsRight?: boolean;
  hasDotsLeftLg?: boolean;
  hasDotsTop?: boolean;
  hasDotsTopLg?: boolean;
  hasDotsBottomLg?: boolean;
  hasDotsFull?: boolean;
  hasDotsColor?: string;
  children?: React.ReactNode;
};

export const ContentBlock: FC<ContentBlockProps> = ({
  wrapperID = '',
  wrapperClass = '',
  bgColor = 'bg-white-blue dark:bg-gray',
  bgHeight = 'h-1/2',
  containerClass = 'bg-transparent',
  partialBg = false,
  hideoverflow = true,
  padtop = true,
  padbottom = true,
  padright = true,
  padleft = true,
  bleedleft = false,
  bleedright = false,
  bleedrightdesktop = false,
  bleedleftdesktop = false,
  hasDots = false,
  hasDotsRight = false,
  hasDotsLeftLg = false,
  hasDotsTop = false,
  hasDotsTopLg = false,
  hasDotsBottomLg = false,
  hasDotsFull = false,
  hasDotsColor = '',
  children,
}) => {
  return (
    <section
      className={classNames('content relative', wrapperClass)}
      {...(wrapperID && { id: wrapperID })}>
      {partialBg && <div className={classNames('absolute inset-0', bgHeight, bgColor)} />}
      <div
        className={classNames('container mx-auto', containerClass, hasDotsColor, {
          'overflow-hidden': hideoverflow,
          'pt-8 lg:pt-16': padtop,
          'pb-8 lg:pb-16': padbottom,
          'pr-4 md:pr-8 lg:pr-16': padright,
          'pl-4 md:pl-8 lg:pl-16': padleft,
          'container--bleed-left': bleedleft,
          'container--bleed-right': bleedright,
          'pr-4 md:pr-8 lg:pr-0': bleedrightdesktop,
          'pl-4 md:pl-8 lg:pl-0': bleedleftdesktop,
          'relative has-dots z-10': hasDots,
          right: hasDotsRight,
          'left-lg': hasDotsLeftLg,
          top: hasDotsTop,
          'top-lg': hasDotsTopLg,
          'bottom-lg': hasDotsBottomLg,
          'full-dots': hasDotsFull,
        })}>
        {children}
      </div>
    </section>
  );
};

type AlertBoxProps = {
  wrapperClass?: string;
  accent?: string;
  title?: string;
  children?: React.ReactNode;
};

export const AlertBox: FC<AlertBoxProps> = ({
  wrapperClass = 'bg-white-blue dark:bg-brand-navy my-6',
  accent = 'accent-red',
  title,
  children,
}) => {
  return (
    <div
      className={classNames(
        'p-4 border-l-4 lg:border-l-8 no-m-last leading-snug shadow-lg',
        'border-' + accent,
        wrapperClass,
      )}>
      {title && <span className="block mb-4 h5 shieldbold text-uppercase">{title}</span>}
      {children}
    </div>
  );
};

type ExpanderProps = {
  title: string | ReactNode;
  wrapperClass?: string;
  titleClass?: string;
  expanderColor?: string;
  children?: React.ReactNode;
};

export const Expander: FC<ExpanderProps> = ({
  wrapperClass = 'bg-white dark:bg-brand-navy',
  titleClass = 'bg-white-blue dark:bg-brand-navy',
  title,
  expanderColor = 'text-brand-bright dark:text-accent-neon',
  children,
}) => {
  return (
    <div className="expander">
      <details
        className={classNames(
          'group border border-accent-lilac dark:border-gray rounded',
          wrapperClass,
        )}>
        <summary
          className={classNames(
            'py-4 px-6 flex label cursor-pointer rounded font-shield-bold',
            titleClass,
          )}>
          <div className="my-auto mr-auto">{title}</div>
          <i
            className={classNames(
              'fas my-auto ml-6 fa-angle-down group-open:rotate-180',
              expanderColor,
            )}
          />
        </summary>
        <div className="py-3 px-6 copy animate animate-fade-in">{children}</div>
      </details>
    </div>
  );
};

type ExternalLinkProps = {
  iconColor?: string;
};

export const ExternalLink: FC<ExternalLinkProps> = ({
  iconColor = 'text-white dark:text-accent-cyan',
}) => {
  return (
    <>
      <div className="inline-block leading-none">
        <i
          className={classNames(
            'fas fa-external-link-alt text-[9px] h-3 opacity-75 self-center ml-[7px] leading-none',
            iconColor,
          )}
        />
        <span className="sr-only">
          {' '}
          Opens an external site in a new window that may not meet accessibility guidelines.
        </span>
      </div>
    </>
  );
};
